import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal, Field } from '../ui/Modal';
import { Icon } from '../ui/Icon';
import { api, ApiError } from '../../lib/api';
import { toast } from '../../stores/toast.store';
import { CourseMode } from '@masamune/shared';

export function NewLessonModal({ onClose, courseId }: { onClose: () => void; courseId: string }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    date: '',
    startTime: '09:30',
    endTime: '13:30',
    topic: '',
    teacherId: '',
    room: '',
    mode: CourseMode.IN_PRESENZA as string,
  });

  const { data: teachers } = useQuery({
    queryKey: ['course-teachers', courseId],
    queryFn: () => api.get<Array<{ id: string; user: { id: string; firstName: string; lastName: string } | null }>>(`/courses/${courseId}/teachers`),
  });

  const create = useMutation({
    mutationFn: () => api.post(`/courses/${courseId}/lessons`, {
      ...form,
      teacherId: form.teacherId || null,
      room: form.room || null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['course', courseId] });
      qc.invalidateQueries({ queryKey: ['attendance-matrix', courseId] });
      toast.ok('Lezione creata', form.topic);
      onClose();
    },
    onError: (err) => toast.alert('Errore creazione lezione', err instanceof ApiError ? err.message : 'Riprova'),
  });

  return (
    <Modal
      title="Nuova lezione"
      kicker="Corso · Calendario"
      onClose={onClose}
      size="md"
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Annulla</button>
          <div className="spacer" />
          <button className="btn btn-primary" onClick={() => create.mutate()} disabled={!form.date || !form.topic || create.isPending}>
            Crea lezione <Icon name="arrowRight" size={12} />
          </button>
        </>
      }
    >
      <div className="form-grid">
        <Field label="Argomento" required span={12}>
          <input className="inp" placeholder="es. SEO tecnico avanzato" value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} autoFocus />
        </Field>
        <Field label="Data" required span={4}>
          <input className="inp mono" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
        </Field>
        <Field label="Inizio" span={4}>
          <input className="inp mono" type="time" value={form.startTime} onChange={(e) => setForm({ ...form, startTime: e.target.value })} />
        </Field>
        <Field label="Fine" span={4}>
          <input className="inp mono" type="time" value={form.endTime} onChange={(e) => setForm({ ...form, endTime: e.target.value })} />
        </Field>
        <Field label="Docente" span={6}>
          <select className="inp" value={form.teacherId} onChange={(e) => setForm({ ...form, teacherId: e.target.value })}>
            <option value="">—</option>
            {(teachers ?? []).filter((t) => t.user).map((t) => (
              <option key={t.id} value={t.user!.id}>{t.user!.firstName} {t.user!.lastName}</option>
            ))}
          </select>
        </Field>
        <Field label="Aula" span={6}>
          <input className="inp" placeholder="Aula Galileo / Remoto" value={form.room} onChange={(e) => setForm({ ...form, room: e.target.value })} />
        </Field>
        <Field label="Modalità" span={12}>
          <div className="seg-control">
            {[CourseMode.IN_PRESENZA, CourseMode.REMOTO, CourseMode.BLENDED, CourseMode.IN_HOUSE].map((m) => (
              <button key={m} aria-pressed={form.mode === m} onClick={() => setForm({ ...form, mode: m })}>
                {m.replace('_', ' ')}
              </button>
            ))}
          </div>
        </Field>
      </div>
    </Modal>
  );
}
